import React from 'react';

interface ErrorMessageProps {
  error: string | Error | null;
  title?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  variant?: 'inline' | 'banner' | 'fullscreen';
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({
  error,
  title = 'An error occurred',
  onRetry,
  onDismiss,
  className = '',
  variant = 'inline'
}) => {
  if (!error) return null;

  const errorMessage = error instanceof Error ? error.message : error;

  // Determine error type for contextual messaging
  const getErrorContext = (msg: string) => {
    const lowerMsg = msg.toLowerCase();
    
    if (lowerMsg.includes('network') || lowerMsg.includes('fetch') || lowerMsg.includes('connection')) {
      return {
        icon: '🌐',
        suggestion: 'Please check your internet connection and try again.',
        type: 'network'
      };
    }
    
    if (lowerMsg.includes('permission') || lowerMsg.includes('unauthorized') || lowerMsg.includes('access denied')) {
      return {
        icon: '🔒',
        suggestion: 'You may need to log in again. Please refresh the page.',
        type: 'auth'
      };
    }
    
    if (lowerMsg.includes('timeout') || lowerMsg.includes('timed out')) {
      return {
        icon: '⏱️',
        suggestion: 'The request took too long. Please try again.',
        type: 'timeout'
      };
    }
    
    if (lowerMsg.includes('not found') || lowerMsg.includes('404')) {
      return {
        icon: '🔍',
        suggestion: 'The requested resource could not be found.',
        type: 'notfound'
      };
    }
    
    return {
      icon: '⚠️',
      suggestion: 'Something unexpected happened. Please try again or contact support if the problem persists.',
      type: 'generic'
    };
  };

  const context = getErrorContext(errorMessage);

  if (variant === 'fullscreen') {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen p-4 bg-luna-bg-light dark:bg-luna-bg-dark ${className}`}>
        <div className="max-w-2xl w-full bg-white dark:bg-luna-primary rounded-xl border border-red-200 dark:border-red-800 shadow-sm p-8">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-4xl">{context.icon}</span>
            <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">
              {title}
            </h1>
          </div>
          
          <p className="text-luna-text-light dark:text-luna-text-dark mb-4">
            {errorMessage}
          </p>
          
          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mb-6">
            {context.suggestion}
          </p>

          <div className="flex gap-3">
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <span className="text-xl">{context.icon}</span>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
              {title}
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-2">
              {errorMessage}
            </p>
            {context.suggestion && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {context.suggestion}
              </p>
            )}
            {(onRetry || onDismiss) && (
              <div className="flex gap-2 mt-3">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="text-xs px-3 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded hover:opacity-80 transition-opacity"
                  >
                    Retry
                  </button>
                )}
                {onDismiss && (
                  <button
                    onClick={onDismiss}
                    className="text-xs px-3 py-1 text-red-700 dark:text-red-300 hover:underline"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline variant (default)
  return (
    <div className={`flex items-center gap-2 text-sm text-red-600 dark:text-red-400 ${className}`}>
      <span>{context.icon}</span>
      <span>{errorMessage}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className="ml-2 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 rounded hover:opacity-80 transition-opacity"
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default ErrorMessage;

